import { CommonCMS } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { H } from "@ecco/ui/a11y-heading";
import { getWalkInDateRange } from "@lib/utils/store-locator-utility/getWalkInDateRange";
import { Text } from "@ecco/ui/text";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { HStack } from "@ecco/ui/stack";
import { use, useEffect, useState } from "react";
import type { WalkInHours } from "@features/stores/types";
import { dayOfWeek } from "@features/stores/constants";
import { Controller } from "./controller";
import { humanizeDistance } from "./humanize-distance";
import { storeBadgeStyles } from "./StoreLocator.style";
import { storeLocator } from "@lib/constants/contentful/storeLocator";
import { activeSiteConfig } from "@config/site/site-config";
import { hoursNotAvailable } from "./utils/hoursNotAvailable";
import { useLocale } from "next-intl";

type AddressWithStocksProps = {
    name: string;
    street: string;
    city: string;
    walkInHours: WalkInHours;
    longitude: number;
    latitude: number;
    localDate: Date;
    isExpanded?: boolean;
    isModal?: boolean;
    isEccoStore?: boolean;
    isPLParcelShop?: boolean;
};

export const AddressWithStocks = ({
    name,
    street,
    city,
    walkInHours,
    latitude,
    longitude,
    isEccoStore,
    localDate,
    isExpanded,
    isModal,
    isPLParcelShop = false,
}: AddressWithStocksProps) => {
    const locale = useLocale();
    const { clientLocation, dayOfWeekLabels, showStoreBadge } = use(Controller);
    const { getMultiple } = useMicrocopy();
    const [distance, setDistance] = useState<string>("");

    const microcopies = getMultiple(CommonCMS.page, [
        ClickAndCollect.storeLocatorOpen,
        ClickAndCollect.storeLocatorOpens,
        ClickAndCollect.storeLocatorClose,
        ClickAndCollect.storeLocatorClosed,
        storeLocator.eccoStore,
        storeLocator.partnerStore,
        storeLocator.hoursNotAvailable,
    ]);

    const dayIndex = localDate.getDay();

    const walkInHoursOfDay = walkInHours?.[dayOfWeek?.[dayIndex]];
    const closingHour = walkInHoursOfDay?.close.trim() !== "" ? walkInHoursOfDay?.close : "00:00";
    const getNextDay = dayIndex === 6 ? 0 : dayIndex + 1;

    const [openDate, closeDate] = getWalkInDateRange(walkInHoursOfDay, localDate);

    const isOpeningNextDay =
        !walkInHours?.[dayOfWeek?.[dayIndex]]?.open || localDate.getTime() >= closeDate.getTime();
    const nextOpeningDayKey = dayOfWeek?.[isOpeningNextDay ? getNextDay : dayIndex];
    const nextOpeningHour = `${walkInHours?.[nextOpeningDayKey]?.open}${
        isOpeningNextDay ? ` ${dayOfWeekLabels?.[nextOpeningDayKey]?.slice(0, 3)}` : ""
    }`;

    const {
        geo: { imperialMetricSystem },
    } = activeSiteConfig;

    const isOpen =
        localDate.getTime() > openDate.getTime() && localDate.getTime() < closeDate.getTime();

    useEffect(() => {
        if (clientLocation) {
            const dist = humanizeDistance(
                {
                    latitude: clientLocation.lat,
                    longitude: clientLocation.lng,
                },
                {
                    latitude,
                    longitude,
                },
                locale,
                !imperialMetricSystem ? "metric" : "imperial"
            );

            setDistance(dist);
        }
    }, [clientLocation, latitude, locale, longitude]);

    return (
        <Box width="100%">
            <H size="h6" mb={isModal ? 4 : 1}>
                {name}
            </H>
            <Text color="gray.500">{street}</Text>
            <Text color="gray.500">{city}</Text>
            {showStoreBadge && (
                <Box sx={storeBadgeStyles(isEccoStore)}>
                    {isEccoStore
                        ? microcopies[storeLocator.eccoStore]
                        : microcopies[storeLocator.partnerStore]}
                </Box>
            )}
            <Box display={isExpanded && "none"} mt={4}>
                <Flex justifyContent="space-between">
                    {!isPLParcelShop && (
                        <HStack>
                            {hoursNotAvailable(walkInHours) ? (
                                <Text pr={5} size="b2">
                                    {microcopies[storeLocator.hoursNotAvailable]}
                                </Text>
                            ) : (
                                <Text size="b2">
                                    {isOpen
                                        ? microcopies[ClickAndCollect.storeLocatorOpen]
                                        : microcopies[ClickAndCollect.storeLocatorClosed]}
                                    <Text as="span" mx={1}>
                                        |
                                    </Text>
                                    {isOpen
                                        ? `${microcopies[ClickAndCollect.storeLocatorClose]} ${closingHour}`
                                        : `${
                                              microcopies[ClickAndCollect.storeLocatorOpens]
                                          } ${nextOpeningHour}`}
                                </Text>
                            )}
                        </HStack>
                    )}
                    {distance && (
                        <Text whiteSpace="nowrap" size="b2">
                            {distance}
                        </Text>
                    )}
                </Flex>
            </Box>
        </Box>
    );
};
